<template>
  <BaseLoading v-if="isGripAnalysisEnablersLoading" class="background-white" />
  <div v-else :class="['flex flex-column top', $style.enablerContainer]">
    <div
      v-for="(item, index) in itemList"
      :key="index"
      :id="'analysis_enabler_' + item.id"
    >
      <b-collapse :open="true" aria-id="contentIdForEnablers" class="width-100">
        <template #trigger="props">
          <div
            v-if="itemList.length > 0 || showEnablerDraggableBox"
            :class="[
              'grip-collapse-container-btn',
              !props.open ? 'border-bottom-Gray86' : null,
            ]"
          >
            <b-icon
              :icon="!props.open ? 'chevron-down' : 'chevron-up'"
              size="is-medium"
            >
            </b-icon>
            <a :class="$style.triggerBtn" aria-controls="contentIdForEnablers">
              Enabler
            </a>
          </div>
        </template>

        <div :class="[$style.itemContainer, 'has-position-relative']">
          <div :class="['flex flex-row top', $style.itemBox]">
            <div>
              <b :class="$style.title">
                {{ `${index + 1}. ` }}
              </b>
            </div>
            <div class="flex cell flex-column">
              <b :class="$style.title">
                {{ item.title }}
              </b>
              <div class="flex flex-row top">
                <p
                  v-if="item.gripDomainCategory"
                  :class="['margin-right8', $style.categoryBox]"
                >
                  {{ getUpperCase(item.gripDomainCategory.name) }}
                </p>
              </div>
              <p :class="$style.description">
                {{ item.description }}
              </p>
            </div>
            <div v-if="!isDataCollector">
              <a @click="handleEdit(item)" :class="$style.editBtn">
                <b-icon icon="pencil" size="is-small" />
              </a>
              <a @click="handleDelete(item.id)" :class="$style.deletebtn">
                <b-icon icon="delete" size="is-small" />
              </a>
            </div>
          </div>
          <!-- Conditionally render overlay if this enabler is being edited and modal is minimized -->
          <div
            v-if="item.id === editingEnablerId && isMinimized"
            class="shared-list-item-edit-overlay"
            @click="restoreEnablerModal"
          >
            <span class="shared-list-item-overlay-text">
              Resume Edit <i class="mdi mdi-arrow-expand"></i>
            </span>
          </div>
        </div>
      </b-collapse>
      <GripAnalysisStrategies
        :projectId="projectId"
        :isDataCollector="isDataCollector"
        :gripAnalysisId="gripAnalysisId"
        :gripAnalysisEnablerId="item.id"
        :gripAnalysisStrategies="item.gripAnalysisStrategies"
        :updatedItem="updatedItem"
        @addGripAnalysisStrategy="handleAddGripAnalysisStrategy"
        @updateGripAnalysisStrategy="handleUpdateGripAnalysisStrategy"
        @deleteGripAnalysisStrategy="handleDeleteGripAnalysisStrategy"
        @handleUpdatedItemReload="handleUpdatedItemReload"
      />
    </div>
    <b-collapse
      v-if="showEnablerDraggableBox && !isDataCollector"
      :open="true"
      aria-id="contentIdForAddEnablers"
      class="width-100"
    >
      <template #trigger="props">
        <div :class="['grip-collapse-container-btn']">
          <b-icon
            :icon="!props.open ? 'chevron-down' : 'chevron-up'"
            size="is-medium"
          >
          </b-icon>
          <a :class="$style.triggerBtn" aria-controls="contentIdForAddEnablers">
            Enabler
          </a>
        </div>
      </template>

      <div
        :class="[
          'flex flex-column top',
          $style.draggableContainer,
          'has-position-relative',
        ]"
      >
        <div
          id="enablerDropZone"
          @dragover="onDragOver($event)"
          @drop="onDrop($event, 'enablerDropZone')"
          :class="['flex flex-column top', $style.labelBox]"
        >
          <p :class="$style.dragDropContainerText">
            Drag and drop existing Enabler
          </p>
          <p :class="$style.dragDropContainerSecondText">
            or
            <a @click="openEnablerModal()" :class="$style.addNewItem"
              >create new</a
            >
          </p>
        </div>

        <div
          v-if="isModalActive && isMinimized && isNew"
          class="shared-list-item-edit-overlay"
          @click="restoreEnablerModal"
        >
          <span class="shared-list-item-overlay-text">
            Resume Create <i class="mdi mdi-arrow-expand"></i>
          </span>
        </div>
      </div>
    </b-collapse>

    <div v-else-if="!isDataCollector" class="padding-16">
      <div :class="$style.addNewItem">
        <a @click="handleShowDraggableBox()">+ Add Enabler</a>
      </div>
    </div>

    <EnablerModal
      v-if="isModalActive"
      :key="modalKey"
      :isModalActive="isModalActive"
      :projectId="projectId"
      :isNew="isNew"
      :draggable="true"
      :gripEnablerDetails="gripEnablerDetailsForEdit"
      :isMinimized="isMinimized"
      @close="closeEnablerModal"
      @create="handleEnablerCreate"
      @update="handleEnablerUpdate"
      @minimize="minimizeEnablerModal"
      @restore="restoreEnablerModal"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  CreateGripAnalysisEnablerPayload,
  CreateGripAnalysisStrategyPayload,
  CreateGripEnablerBase,
  GripAnalysisEnabler,
  GripItemType,
  GripEnablerDTO,
  GripEnablerStatus,
  GripItemReloadDTO,
  UpdateGripAnalysisStrategyPayload,
  UpdateGripEnablerBase,
} from '../../../store/types/grip.types';
import BaseLoading from '../../../components/base/BaseLoading.vue';
import EnablerModal from '../Enabler/EnablerModal.vue';
import DeleteModalBox from '../../../components/DeleteModalBox.vue';
import GripAnalysisStrategies from './GripAnalysisStrategies.vue';
import { namespace } from 'vuex-class';
import { showUnsavedChangesDialog } from '@/utils/dialog.util';

const unsavedChangesGuard = namespace('unsavedChangesGuard');

@Component({
  components: {
    BaseLoading,
    EnablerModal,
    DeleteModalBox,
    GripAnalysisStrategies,
  },
})
export default class GripAnalysisEnablers extends Vue {
  @Prop() public projectId!: number;
  @Prop() public isDataCollector!: boolean;
  @Prop() public gripAnalysisId!: number;
  @Prop() public gripAnalysisBarrierId!: number;
  @Prop() public gripAnalysisEnablers!: GripAnalysisEnabler[];
  @Prop() public updatedItem!: GripItemReloadDTO;
  public isGripAnalysisEnablersLoading: boolean = false;
  public showEnablerDraggableBox: boolean = false;
  public itemList: GripAnalysisEnabler[] = [];
  public tempDirtyEnabler: CreateGripEnablerBase = {
    title: '',
    description: '',
    links: [],
    attachments: [],
    gripDomainCategoryId: null,
  };
  public modalKey: number = Math.floor(Math.random() * 999);
  public isModalActive: boolean = false;
  public isNew: boolean = false;
  public gripEnablerDetailsForEdit: GripEnablerDTO | null = null;
  public isMinimized: boolean = false;

  @unsavedChangesGuard.State('userSelectedKeepEditing')
  public userSelectedKeepEditing!: boolean;

  @unsavedChangesGuard.Action('notifyKeepEditingSelected')
  public notifyKeepEditingSelected!: () => void;

  @unsavedChangesGuard.Action('resetKeepEditingFlag')
  public resetKeepEditingFlag!: () => void;

  get editingEnablerId(): number | null {
    return this.isModalActive && !this.isNew && this.gripEnablerDetailsForEdit
      ? this.gripEnablerDetailsForEdit.id
      : null;
  }

  public getUpperCase(value: string): string {
    return value.toUpperCase();
  }

  public updated(): void {
    if (this.updatedItem.item === GripItemType.AnalysisEnabler) {
      const element = document.getElementById(
        this.updatedItem.item + '_' + this.updatedItem.id,
      );
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else if (this.updatedItem.prevItemId) {
        const prevElement = document.getElementById(
          this.updatedItem.prevItemId as string,
        );
        if (prevElement) {
          prevElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }

    if (this.showEnablerDraggableBox) {
      const element = document.getElementById('contentIdForAddEnablers');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  public handleShowDraggableBox(): void {
    this.showEnablerDraggableBox = true;
  }

  public onDragOver(event: any): void {
    const dragSupported = event.dataTransfer.types.length;
    if (dragSupported) {
      event.dataTransfer.dropEffect = 'copy';
      event.preventDefault();
    }
  }

  public onDrop(event: any, id: string): void {
    event.preventDefault();
    const jsonData = event.dataTransfer.getData('application/json');
    const data = JSON.parse(jsonData);
    if (!data) {
      return;
    }

    const { itemType, ...enablerData } = data;
    if (id === 'enablerDropZone' && itemType === 'enabler') {
      const enablerPayload: CreateGripAnalysisEnablerPayload = {
        ...enablerData,
        gripAnalysisId: this.gripAnalysisId,
        gripAnalysisBarrierId: this.gripAnalysisBarrierId,
      };
      this.$emit('addGripAnalysisEnabler', { enablerPayload, isNew: false });
    }
  }

  private _resetLocalModalState(): void {
    this.isModalActive = false;
    this.isMinimized = false;
    this.gripEnablerDetailsForEdit = null;
    this.isNew = false;
    this.modalKey += 1;
  }

  public openEnablerModal(): void {
    // MODIFICATION START: Update openEnablerModal logic
    this._resetLocalModalState();
    this.isNew = true;
    this.isModalActive = true;
  }

  public handleEdit(enabler: GripAnalysisEnabler): void {
    const {
      gripAnalysisStrategies,
      status,
      createdDate,
      updatedDate,
      ...enablerDetails
    } = enabler;

    this._resetLocalModalState();
    this.gripEnablerDetailsForEdit = enablerDetails as GripEnablerDTO;
    this.isNew = false;
    this.isModalActive = true;
  }

  public async closeEnablerModal(): Promise<void> {
    this._resetLocalModalState();
  }

  public minimizeEnablerModal(): void {
    this.isMinimized = true;
  }

  public restoreEnablerModal(): void {
    this.isMinimized = false;
  }

  public handleEnablerCreate(gripEnabler: CreateGripEnablerBase): void {
    const enablerPayload: CreateGripAnalysisEnablerPayload = {
      ...gripEnabler,
      gripAnalysisId: this.gripAnalysisId,
      gripAnalysisBarrierId: this.gripAnalysisBarrierId,
    };
    this.$emit('addGripAnalysisEnabler', {
      enablerPayload,
      isNew: true,
    });
    this._resetLocalModalState();
    this.showEnablerDraggableBox = false;
  }

  public handleEnablerUpdate(
    gripEnablerUpdateData: UpdateGripEnablerBase & { id: number },
  ): void {
    const { id, ...changesToApply } = gripEnablerUpdateData;
    let actualChangesExist = Object.keys(changesToApply).length > 0;

    if (this.gripEnablerDetailsForEdit) {
      let hasMeaningfulChange = false;
      for (const key in changesToApply) {
        if (changesToApply.hasOwnProperty(key)) {
          if (
            JSON.stringify((this.gripEnablerDetailsForEdit as any)[key]) !==
            JSON.stringify((changesToApply as any)[key])
          ) {
            hasMeaningfulChange = true;
            break;
          }
        }
      }
      actualChangesExist = hasMeaningfulChange;
    }

    if (actualChangesExist) {
      this.$emit('updateGripAnalysisEnabler', {
        ...gripEnablerUpdateData, // Send all data including ID
        gripAnalysisId: this.gripAnalysisId,
        gripAnalysisBarrierId: this.gripAnalysisBarrierId,
      });
      this.$emit('handleUpdatedItemReload', {
        item: GripItemType.AnalysisEnabler,
        id: gripEnablerUpdateData.id,
        prevItemId: this.getPrevItemId(gripEnablerUpdateData.id),
      });
    }
    // Always close modal and notify, regardless of whether an API call was made.
    this._resetLocalModalState();
  }

  public handleDelete(gripAnalysisEnablerId: number): void {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete Enabler?',
        modalContent:
          'Are you sure you want to delete this enabler? This action cannot be undone.',
      },
      events: {
        close: () => {
          this.$emit('close');
        },
        confirm: () => {
          this.$emit('deleteGripAnalysisEnabler', gripAnalysisEnablerId);
          this.$emit('handleUpdatedItemReload', {
            item: GripItemType.AnalysisEnabler,
            id: gripAnalysisEnablerId,
            prevItemId: this.getPrevItemId(gripAnalysisEnablerId),
          });
        },
      },
    });
  }

  public getPrevItemId(gripEnablerId: number): string {
    const enablerIndex: number = this.itemList.findIndex(
      (item) => item.id === gripEnablerId,
    );

    return enablerIndex > 0
      ? GripItemType.AnalysisEnabler + '_' + this.itemList[enablerIndex - 1].id
      : GripItemType.AnalysisBarrier + '_' + this.gripAnalysisBarrierId;
  }

  public handleUpdatedItemReload(data: GripItemReloadDTO): void {
    this.$emit('handleUpdatedItemReload', data);
  }

  public handleAddGripAnalysisStrategy(strategyDetails: {
    strategyPayload: CreateGripAnalysisStrategyPayload;
    isNew: boolean;
  }): void {
    this.$emit('addGripAnalysisStrategy', strategyDetails);
  }

  public handleUpdateGripAnalysisStrategy(
    strategyPayload: UpdateGripAnalysisStrategyPayload,
  ): void {
    this.$emit('updateGripAnalysisStrategy', strategyPayload);
  }

  public handleDeleteGripAnalysisStrategy(
    gripAnalysisStrategyId: number,
  ): void {
    this.$emit('deleteGripAnalysisStrategy', gripAnalysisStrategyId);
  }

  @Watch('gripAnalysisEnablers', { deep: true, immediate: true })
  public onGripAnalysisEnablersChanged(
    newEnablers: GripAnalysisEnabler[] | undefined,
  ): void {
    if (newEnablers) {
      // Enablers are already filtered for ACTIVE status by GripAnalysisPane.
      this.itemList = [...newEnablers].sort((a, b) => a.id - b.id);
    } else {
      this.itemList = [];
    }
    // Note: showEnablerDraggableBox is not updated here as its logic
    // seems to be more manually controlled in this component.
  }

  @Watch('userSelectedKeepEditing')
  public onUserSelectedKeepEditing(newValue: boolean): void {
    this.resetKeepEditingFlag();
    if (newValue && this.isMinimized && this.isModalActive) {
      this.restoreEnablerModal();
    }
  }
}
</script>

<style lang="scss" module>
.enablerContainer {
  width: 100%;
  padding-left: 20px;
}

.triggerBtn {
  background: none;
  border: none;
  padding: 10px 0px 0px 0px;
  margin-left: unset;
  font-weight: 400;
  font-size: 14px;
  color: #444f51;

  &:focus {
    box-shadow: none;
    border: none;
  }

  &:hover,
  &:active {
    border: none;
  }
}

.draggableContainer {
  justify-content: center;
  align-items: center;
  padding: 36px 20px;
  background: #fbfbfb;
  border: 1px dashed #9f9f9f;
  border-radius: 6px;
  box-sizing: border-box;

  .labelBox {
    align-items: center;

    .dragDropContainerText {
      font-family: 'Open Sans';
      font-style: normal;
      font-weight: 700;
      font-size: 18px;
      line-height: 22px;
      color: #9f9f9f;
      margin-bottom: 0.5rem;
    }

    .dragDropContainerSecondText {
      font-weight: 400;
      font-size: 16px;
      line-height: 22px;
      color: #444f51;
    }
  }
}

.itemContainer {
  margin-bottom: 1rem;
}

.itemBox {
  padding: 16px;
  background: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  box-sizing: border-box;

  .title {
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: #444f51;
    margin-bottom: 0.5rem;
    margin-right: 0.5rem;
    max-width: 70ch;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .categoryBox {
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    background: #e6ecf6;
    border-radius: 4px;
    margin-bottom: 0.5rem;
    color: #444f51;
    width: fit-content;
    padding: 2px 4px;
  }

  .description {
    max-width: 70ch;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-right: 0.5rem;
  }

  .editBtn {
    color: #21b5ea;
    padding-right: 12px;
  }
  .deletebtn {
    color: #21b5ea;
  }
}

.addNewItem {
  color: #00205b;
  font-weight: bold;
  font-size: 14px;
}
</style>
